import React, { useState, useEffect } from 'react';
import { X, Link2, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const ShareModal = ({ isOpen, onClose, listing }) => {
  const [copied, setCopied] = useState(false);

  // Body scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !listing) return null;

  // Use SEO-friendly slug if available, fallback to ID
  const listingPath = listing.slug || listing.id;
  const shareUrl = `${window.location.origin}/listing/${listingPath}`;
  const shareTitle = listing.title;
  const shareText = `Check out this listing on Your Buy Site: ${listing.title}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Native share API for mobile
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      // Fallback to copy
      copyToClipboard();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-card rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">Share Listing</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-full transition-colors"
            data-testid="share-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Listing Preview */}
        <div className="p-4 border-b border-border">
          <div className="flex gap-3">
            {listing.images?.[0] ? (
              <img 
                src={listing.images[0]} 
                alt={listing.title}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-secondary flex items-center justify-center">
                <Share2 className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-foreground line-clamp-1">{listing.title}</h4>
              <p className="text-sm text-muted-foreground line-clamp-2">{listing.description}</p>
            </div>
          </div>
        </div>

        {/* Share Button */}
        <div className="p-4 space-y-4">
          {/* Main Share Button */}
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            data-testid="share-button"
          >
            <Share2 className="w-5 h-5" />
            Share
          </button>

          {/* Copy Link */}
          <div className="relative">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="w-full pr-24 py-3 px-4 bg-secondary border border-border rounded-xl text-sm text-foreground"
              data-testid="share-url-input"
            />
            <button
              onClick={copyToClipboard}
              className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
              data-testid="copy-link-button"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
